import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Banner, Button, TextField } from "@shopify/polaris";
import { MissingInvoiceExpectation } from "types/InvoiceInbox";
import { fetchInvoiceExpectations, waiveInvoiceExpectation } from "services/invoice-inbox.service";
import { formatPrettyDate } from "utils/helperFunctions";

/**
 * Monthly invoices that have not arrived.
 *
 * A supplier marked as invoicing every month (the checkbox on the document
 * panel) whose invoice for some month is already past due and not in the
 * register. Nobody misses an invoice that never came, so this sits above the
 * register on every tab and in both views.
 *
 * It refreshes on the register's reloadToken and, quietly, on its own.
 *
 * It is a warning, not a section: while it loads, when nothing is missing and
 * when the list could not be fetched it renders nothing at all. An empty box
 * or a spinner above the register on every visit would teach people to look
 * past the one time it matters.
 */

type Props = {
  companyId: string;
  /** Bumped by the register whenever a document changes: a new invoice can
   * close a gap, and the panel's monthly toggle can open or close several. */
  reloadToken: number;
};

// How long the "dismissed" toast stays up. It carries the only way back from
// a dismissal made by mistake, so it has to outlast a moment of hesitation.
const UNDO_TOAST_MS = 10000;

// How often the banner refreshes itself, the same pace as the register's idle
// refresh — see the effect that uses it.
const REFRESH_MS = 60000;

// The width of purchase_expectation_waivers.note; a longer note would fail the
// insert and come back as a bare server error.
const NOTE_MAX_LENGTH = 500;

const rowKey = (row: { supplier: string; period: string }) => `${row.supplier}|${row.period}`;

/**
 * 'YYYY-MM' as a month and year in the reader's language ("September 2026",
 * "2026 m. rugsėjis"). Built and formatted in UTC, so no time zone can push
 * the first of the month into the one before it. The Lithuanian form is the
 * nominative, so translations put it where no word has to agree with it.
 */
export function periodLabel(period: string, language: string): string {
  const [year, month] = period.split("-").map(Number);
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(language === "lt" ? "lt-LT" : "en-GB", {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

export default function InvoiceInboxExpectations({ companyId, reloadToken }: Props) {
  const { t, i18n } = useTranslation();
  // Tagged with the company it was fetched for: switching companies must not
  // show the previous one's suppliers for as long as the next fetch takes —
  // or for good, if that fetch fails.
  const [loaded, setLoaded] = useState<{ companyId: string; missing: MissingInvoiceExpectation[] } | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      const data = await fetchInvoiceExpectations(companyId);
      // A slower earlier request must not overwrite a newer one — in
      // particular the one that confirms a dismissal just made.
      if (requestId !== requestRef.current) return;
      setLoaded({ companyId, missing: data.missing || [] });
    } catch (err) {
      // Whatever was on screen stays: a failed refresh is no evidence that
      // the invoices arrived, and a red banner over the register for a side
      // warning would be louder than the warning itself.
      console.error("Failed to load missing monthly invoices", err);
    }
  }, [companyId]);

  // Undo and dismissal outlive the render they were started from; they reload
  // through this so a company switched in the meantime is not answered with
  // the old company's list, and its own in-flight fetch is not cancelled.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    setOpenKey(null);
    setNote("");
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  // The token only moves on what the register itself sees: the page and tab
  // on screen. An invoice that arrives anywhere else — another tab, a later
  // page, the month view, which does not refresh itself at all — would stay
  // "missing" here until a reload, inviting someone to dismiss a month that
  // has in fact arrived. Never on a hidden tab: nobody is looking.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) loadRef.current();
    };
    const intervalId = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const language = (i18n as any).language as string;
  const missing = loaded && loaded.companyId === companyId ? loaded.missing : [];
  if (!missing.length) return null;

  const supplierLabel = (row: MissingInvoiceExpectation) =>
    row.supplierName ||
    t("invoice_inbox_expectations_unnamed_supplier", { defaultValue: "Unnamed supplier" });

  const serverMessage = (err: any) => {
    const data = err && err.response && err.response.data;
    return (
      (data && (data.message || data.code)) ||
      t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." })
    );
  };

  const undo = async (forCompany: string, row: MissingInvoiceExpectation) => {
    try {
      await waiveInvoiceExpectation(forCompany, { supplierId: row.supplier, period: row.period, waive: false });
      toast.success(
        t("invoice_inbox_expectations_restored", {
          defaultValue: "{{supplier}}, {{period}}: the invoice is expected again.",
          supplier: supplierLabel(row),
          period: periodLabel(row.period, language),
        })
      );
    } catch (err) {
      toast.error(serverMessage(err));
    }
    loadRef.current();
  };

  const waive = async (row: MissingInvoiceExpectation) => {
    const key = rowKey(row);
    const forCompany = companyId;
    const trimmed = note.trim();
    setSavingKey(key);
    try {
      await waiveInvoiceExpectation(forCompany, {
        supplierId: row.supplier,
        period: row.period,
        ...(trimmed ? { note: trimmed } : {}),
      });
      // Gone at once rather than after the reload below: the row the person
      // just dismissed staying on screen reads as "that did not work".
      setLoaded((prev) =>
        prev && prev.companyId === forCompany
          ? { companyId: forCompany, missing: prev.missing.filter((m) => rowKey(m) !== key) }
          : prev
      );
      setOpenKey(null);
      setNote("");
      toast.success(
        t("invoice_inbox_expectations_waived", {
          defaultValue: "{{supplier}}, {{period}}: no invoice expected.",
          supplier: supplierLabel(row),
          period: periodLabel(row.period, language),
        }),
        {
          duration: UNDO_TOAST_MS,
          action: {
            label: t("invoice_inbox_expectations_undo", { defaultValue: "Undo" }),
            onClick: () => undo(forCompany, row),
          },
        }
      );
      loadRef.current();
    } catch (err) {
      toast.error(serverMessage(err));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Banner
      tone="warning"
      title={t("invoice_inbox_expectations_title", {
        defaultValue: "Monthly invoices that have not arrived ({{count}})",
        count: missing.length,
      })}
    >
      <p className="text-sm">
        {t("invoice_inbox_expectations_intro", {
          defaultValue:
            "These suppliers invoice every month, but the invoice for the month shown is past the day it usually arrives and is not in the register.",
        })}
      </p>
      <ul className="mt-2 divide-y divide-amber-200">
        {missing.map((row) => {
          const key = rowKey(row);
          const open = openKey === key;
          const period = periodLabel(row.period, language);
          return (
            <li key={key} className="py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <Link
                    to={`/app/${companyId}/customers/${row.supplier}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {supplierLabel(row)}
                  </Link>
                  <span className="ml-2 font-semibold">{period}</span>
                  <div className="text-xs text-gray-600">
                    {t("invoice_inbox_expectations_expected_by", {
                      defaultValue: "expected by {{date}}",
                      date: formatPrettyDate(row.expectedBy),
                    })}
                    {row.lastSeenPeriod &&
                      " · " +
                        t("invoice_inbox_expectations_last_seen", {
                          defaultValue: "last invoice was for {{period}}",
                          period: periodLabel(row.lastSeenPeriod, language),
                        })}
                  </div>
                </div>
                {!open && (
                  // One dismissal at a time: the form's note and the row it
                  // closes on success are shared, so opening another row
                  // mid-save would have its typed note wiped by the first.
                  <Button
                    variant="plain"
                    disabled={savingKey !== null}
                    onClick={() => {
                      setOpenKey(key);
                      setNote("");
                    }}
                  >
                    {t("invoice_inbox_expectations_waive", { defaultValue: "No invoice for this month" })}
                  </Button>
                )}
              </div>
              {open && (
                <div className="mt-2 space-y-2 rounded border border-amber-200 bg-white p-3">
                  <p className="text-sm text-gray-700">
                    {t("invoice_inbox_expectations_waive_explain", {
                      defaultValue:
                        "Only this month ({{period}}) is dismissed for this supplier. The supplier stays marked as invoicing every month, and the following months are checked as usual.",
                      period,
                    })}
                  </p>
                  <TextField
                    label={t("invoice_inbox_expectations_note", { defaultValue: "Note (optional)" })}
                    value={note}
                    onChange={setNote}
                    maxLength={NOTE_MAX_LENGTH}
                    showCharacterCount
                    placeholder={t("invoice_inbox_expectations_note_placeholder", {
                      defaultValue: "e.g. the service was paused that month",
                    })}
                    autoComplete="off"
                  />
                  <div className="flex gap-2">
                    <Button variant="primary" loading={savingKey === key} onClick={() => waive(row)}>
                      {t("invoice_inbox_expectations_waive_confirm", { defaultValue: "Dismiss this month" })}
                    </Button>
                    <Button disabled={savingKey === key} onClick={() => setOpenKey(null)}>
                      {t("cancel", { defaultValue: "Cancel" })}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Banner>
  );
}
